import React, { memo } from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { RenderMemberList } from "components/DisplayBox";
import { colors, margins } from "style";
import { TableRow } from "components/Table";
import { LegacyDataCell, LegacyLoadingRow, MemberCellContent, TermSheetInput } from "components/TermSheets";
import { LEGACY_TERMSHEET_COLUMNS_CONFIG } from "constants/index";
import Icon, { ICON_TYPE, ICON_SIZE } from "components/Icon";
import { useTermSheetCellOperations, useTermSheetContext, useTermSheetHandlers } from "hooks";

/**
 * LegacyTermSheetTableRow
 * Renders a single row in the termsheet table
 * Memoized for performance - only re-renders when props change
 *
 * @param {Object}      assignment
 * @param {Function}    onRemoveMember
 * @param {Boolean}     loading
 */
const LegacyTermSheetTableRow = memo(({ assignment, onRemoveMember, loading, ...props }) => {
  if (loading) return <LegacyLoadingRow />;

  const { termSheet, isDraft } = useTermSheetContext();
  const { getCellValue, hasChanges } = useTermSheetCellOperations();
  const { handleChange: onCellChange, handleRevert: onCellRevert } = useTermSheetHandlers();

  return (
    <TableRow disableAlternateRowColor>
      {LEGACY_TERMSHEET_COLUMNS_CONFIG.map(
        ({
          key,
          width,
          sticky,
          type,
          derivedField,
          currencyField,
          conversionConfig,
          currencyKey,
          readOnlyFunc,
          valueFunc,
          highlightColor,
          ...rest
        }) => (
          <LegacyDataCell
            key={`${assignment.id}-${key}`}
            width={width}
            marginSize={margins.normal}
            highlightColor={highlightColor}
          >
            {sticky ? (
              <MemberCellContent>
                <RenderMemberList data={assignment.profile} />
                {isDraft && onRemoveMember && (
                  <RemoveButton onClick={() => onRemoveMember(assignment)} title="Remove member">
                    <Icon type={ICON_TYPE.close} size={ICON_SIZE.small} />
                  </RemoveButton>
                )}
              </MemberCellContent>
            ) : (
              <TermSheetInput
                type={typeof type === "function" ? type({ termSheet }) : type}
                name={`${key}-${assignment.id}`}
                value={
                  valueFunc?.(getCellValue(assignment, { key, ...rest }), { termSheet }) ||
                  getCellValue(assignment, { key, ...rest })
                }
                onChange={(_, value) => onCellChange(assignment.id, key, value)}
                onRevert={() => onCellRevert(assignment.id, key)}
                hasChanges={
                  !derivedField &&
                  (hasChanges(assignment.id, key) || (currencyKey && hasChanges(assignment.id, currencyKey)))
                }
                currency={currencyField?.({
                  ...props,
                  ...assignment,
                  getCellValue: (fieldKey) => getCellValue(assignment, { key: fieldKey }),
                })}
                fromCurrency={conversionConfig?.fromCurrency?.({
                  ...props,
                  ...assignment,
                  getCellValue: (fieldKey) => getCellValue(assignment, { key: fieldKey }),
                })}
                onCurrencyChange={
                  currencyKey ? (newCurrency) => onCellChange(assignment.id, currencyKey, newCurrency) : undefined
                }
                readOnly={readOnlyFunc?.({ termSheet }) || derivedField}
                highlightColor={highlightColor}
                iconColor={colors.secondary}
                {...rest}
              />
            )}
          </LegacyDataCell>
        )
      )}
    </TableRow>
  );
});

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  color: ${colors.grayAnatomyDark2};

  &:hover {
    background: ${colors.grayAnatomyLight4};
    border-color: ${colors.grayAnatomyLight3};
    color: ${colors.danger};
  }
`;

LegacyTermSheetTableRow.displayName = "LegacyTermSheetTableRow";

LegacyTermSheetTableRow.propTypes = {
  assignment: PropTypes.object,
  onRemoveMember: PropTypes.func,
  loading: PropTypes.bool,
};

export default LegacyTermSheetTableRow;
